import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
} from "react";

import useInboxWsToken from "common/datahooks/useInboxWsToken";
import useWebsocket from "common/hooks/useWebSocket";

import useInboxTypingStore from "../hooks/useInboxTypingStore";

interface InboxEventsWebsocketProviderProps {
  children: ReactNode;
}

const InboxEventsWebsocketContext = createContext<WebSocket>(null);

type InboxEventData =
  | { type: "unread"; unread_count: number; refetch: boolean }
  | {
      type: "typing";
      profile_urn: string;
      conversation_urn: string;
    }
  | { type: "sync"; account_id: number; last_synced_at: number }
  | { type: "sn_sync"; account_id: number; last_synced_at: number };

export function InboxEventsWebsocketProvider({
  children,
}: InboxEventsWebsocketProviderProps) {
  const stopTypingTimeoutRefs = useRef<Record<string, Record<string, number>>>(
    {},
  );

  const { inboxWsToken } = useInboxWsToken();
  const { websocket } = useWebsocket<InboxEventData>(
    inboxWsToken &&
      `${import.meta.env.VITE_INBOX_WS_URL}/events?authorization=${inboxWsToken}`,
    (data) => {
      if (data.type === "typing") {
        const { conversation_urn, profile_urn } = data;

        const { setConversationTypingAccounts } =
          useInboxTypingStore.getState();

        setConversationTypingAccounts(conversation_urn, (prevAccounts) =>
          prevAccounts.includes(profile_urn)
            ? prevAccounts
            : [...prevAccounts, profile_urn],
        );
        if (stopTypingTimeoutRefs.current[conversation_urn]) {
          clearTimeout(
            stopTypingTimeoutRefs.current[conversation_urn][profile_urn],
          );
        }

        if (!stopTypingTimeoutRefs.current[conversation_urn]) {
          stopTypingTimeoutRefs.current[conversation_urn] = {};
        }
        stopTypingTimeoutRefs.current[conversation_urn][profile_urn] =
          window.setTimeout(
            () =>
              setConversationTypingAccounts(conversation_urn, (prevAccounts) =>
                prevAccounts.filter((urn) => urn !== profile_urn),
              ),
            6000,
          );
      }
    },
  );

  return (
    <InboxEventsWebsocketContext.Provider value={websocket}>
      {children}
    </InboxEventsWebsocketContext.Provider>
  );
}

export function useInboxEventsWebsocketContext(
  onEvent: (event: InboxEventData) => void,
) {
  const inboxEventsWebsocket = useContext(InboxEventsWebsocketContext);

  useEffect(() => {
    if (inboxEventsWebsocket) {
      function onMessage(event: MessageEvent) {
        const data = JSON.parse(event.data) as InboxEventData;

        onEvent(data);
      }

      inboxEventsWebsocket.addEventListener("message", onMessage);
      return () => {
        inboxEventsWebsocket.removeEventListener("message", onMessage);
      };
    }
  }, [inboxEventsWebsocket, onEvent]);
}
